import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, GripHorizontal, Loader2, MessageSquare, Mic, Paperclip, Plus, TriangleAlert, X } from 'lucide-react';
import { apiGet, apiPost, apiPostStream } from '../../lib/api-client';
import { formatMoney } from '../../lib/money';
import { apiError } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { ChatTranscript } from './ChatTranscript';
import type { ChatCursorEvent } from './chat-transcript';
import { Button } from '../ui/Button';
import { Select, TextArea } from '../ui/Input';
import { clampGeom, GEOM_KEY, loadGeom, type PanelGeom } from './panel-geom';

interface ChatRole { code: string; title: string; model: string; tools: string[] }
interface ChatSessionRow { id: string; roleCode: string; title: string; model: string; agentId: string; runtime: 'local' | 'cloud'; messages?: { role: 'user' | 'assistant'; text: string; events?: ChatCursorEvent[] }[] }
interface ChatMsg { id: string; role: 'user' | 'assistant'; text: string; files?: string[]; events?: ChatCursorEvent[]; streaming?: boolean }
interface PendingFile { name: string; mimeType: string; dataBase64: string }

export function CollaborateDock() {
  const { can, user, collaborateOpen, closeCollaborate, notifyLedger } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const logRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [role, setRole] = useState('');
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [conversationId, setConversationId] = useState('');
  const [sessions, setSessions] = useState<ChatSessionRow[]>([]);
  const [runMeta, setRunMeta] = useState<{ model: string; agentId: string; runtime: string } | null>(null);
  const [roles, setRoles] = useState<ChatRole[]>([]);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [canvas, setCanvas] = useState<Record<string, unknown> | null>(null);
  const [confirm, setConfirm] = useState<{ tool?: string; confirmId?: string } | null>(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [geom, setGeom] = useState<PanelGeom>(() => loadGeom());
  const drag = useRef<{ kind: 'move' | 'resize'; ox: number; oy: number; start: PanelGeom } | null>(null);
  function scrollDown() {
    queueMicrotask(() => {
      if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    });
  }

  function applyStreamEvent(id: string, event: Record<string, unknown>) {
    const type = String(event.type || '');
    if (type === 'done') {
      setMessages((list) => list.map((message) => (message.id === id ? { ...message, text: String(event.output || message.text), streaming: false } : message)));
      return;
    }
    if (type === 'error') {
      const text = String(event.error || 'Chat failed');
      setMessages((list) => list.map((message) => (message.id === id ? { ...message, text, streaming: false } : message)));
      setError(text);
      return;
    }
    if (type === 'result') return;
    setMessages((list) => list.map((message) => {
      if (message.id !== id) return message;
      const events = [...(message.events || []), event];
      const text = type === 'assistant_delta' ? `${message.text}${String(event.text || '')}` : message.text;
      return { ...message, events, text, streaming: true };
    }));
    scrollDown();
  }

  function loadSessions(code = role) {
    if (!code) return;
    void apiGet<ChatSessionRow[]>(`/api/chat/sessions?roleCode=${encodeURIComponent(code)}`).then((rows) => setSessions(rows || []));
  }

  useEffect(() => {
    void apiGet<ChatRole[]>('/api/chat/roles').then((rows) => {
      setRoles(rows || []);
      const code = rows?.[0]?.code || '';
      setRole((current) => current || code);
      if (code) loadSessions(code);
    });
    const onResize = () => setGeom((current) => clampGeom(current));
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  if (!can('chat') || !user) return null;

  const selected = roles.find((item) => item.code === role);
  const toolCount = !selected ? 0 : selected.tools?.includes('*') ? 'all' : selected.tools?.length || 0;
  const modelLabel = runMeta?.model || selected?.model || '';
  const runLabel = !modelLabel && !runMeta?.agentId ? '' : runMeta?.runtime === 'cloud' && runMeta.agentId ? `${modelLabel} · ${runMeta.agentId}` : runMeta?.runtime ? `${modelLabel} · ${runMeta.runtime}` : modelLabel;

  function newChat() {
    setConversationId('');
    setMessages([]);
    setCanvas(null);
    setConfirm(null);
    setRunMeta(null);
  }

  function applyUi(ui?: { resources?: string[]; navigate?: string }) {
    if (!ui) return;
    if (ui.resources?.length) notifyLedger(ui.resources);
    if (ui.navigate) navigate(ui.navigate);
  }

  function pageContext() {
    const path = location.pathname;
    const recordId = path.split('/')[2];
    const title = document.querySelector('h1')?.textContent?.trim() || path;
    return { path, title, recordId: recordId && recordId !== path.slice(1) ? recordId : undefined };
  }

  async function send() {
    const text = draft.trim();
    if ((!text && !files.length) || sending) return;
    const attached = files;
    const assistantId = `a-${Date.now()}`;
    setMessages((rows) => [...rows, { id: `u-${Date.now()}`, role: 'user', text, files: attached.map((file) => file.name) }, { id: assistantId, role: 'assistant', text: '', streaming: true }]);
    setDraft('');
    setFiles([]);
    setSending(true);
    setError('');
    scrollDown();
    try {
      await apiPostStream('/api/chat/message/stream', {
        roleCode: role,
        message: text,
        conversationId: conversationId || undefined,
        pageContext: pageContext(),
        attachments: attached.map((file) => ({ name: file.name, mimeType: file.mimeType, dataBase64: file.dataBase64 })),
      }, (event) => {
        if (event.conversationId) setConversationId(String(event.conversationId));
        if (event.model || event.agentId || event.runtime) {
          setRunMeta({ model: String(event.model || selected?.model || ''), agentId: String(event.agentId || ''), runtime: String(event.runtime || 'local') });
        }
        if (event.canvas) setCanvas(event.canvas as Record<string, unknown>);
        const ui = event.ui as { confirm?: { tool?: string; confirmId?: string }; resources?: string[]; navigate?: string } | undefined;
        if (ui?.confirm) setConfirm(ui.confirm);
        applyUi(ui);
        applyStreamEvent(assistantId, event);
      });
      setSending(false);
      loadSessions();
    } catch (err) {
      setSending(false);
      const message = apiError(err, 'Chat failed');
      setError(message);
      setMessages((rows) => rows.map((row) => (row.id === assistantId ? { ...row, text: row.text || message, streaming: false } : row)));
    }
  }

  async function doConfirm() {
    if (!confirm?.confirmId) return;
    const assistantId = `a-${Date.now()}`;
    setMessages((rows) => [...rows, { id: assistantId, role: 'assistant', text: '', streaming: true }]);
    try {
      const response = await apiPost<Record<string, unknown>>('/api/chat/confirm', { confirmId: confirm.confirmId });
      setConfirm(null);
      setMessages((rows) => rows.map((message) => (message.id === assistantId ? { ...message, text: String(response.replyText || 'Confirmed.'), streaming: false } : message)));
      if (response.canvas) setCanvas(response.canvas as Record<string, unknown>);
      applyUi(response.ui as { resources?: string[]; navigate?: string } | undefined);
    } catch (err) {
      setError(apiError(err, 'Confirm failed'));
    }
  }

  function pickFiles(list: FileList | null) {
    for (const file of Array.from(list || [])) {
      if (files.length >= 4) {
        setError('Attach at most 4 files');
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        setError(`${file.name} is over 20 MB`);
        continue;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const raw = String(reader.result || '');
        const dataBase64 = raw.includes(',') ? raw.split(',')[1] : raw;
        setFiles((rows) => [...rows.filter((item) => item.name !== file.name), { name: file.name, mimeType: file.type, dataBase64 }]);
      };
      reader.readAsDataURL(file);
    }
  }

  function beginDrag(kind: 'move' | 'resize', event: React.PointerEvent) {
    if (window.matchMedia('(max-width: 767px)').matches) return;
    drag.current = { kind, ox: event.clientX, oy: event.clientY, start: geom };
    const move = (ev: PointerEvent) => {
      if (!drag.current) return;
      const dx = ev.clientX - drag.current.ox;
      const dy = ev.clientY - drag.current.oy;
      const start = drag.current.start;
      setGeom(drag.current.kind === 'move' ? clampGeom({ ...start, x: start.x + dx, y: start.y + dy }) : clampGeom({ ...start, w: start.w + dx, h: start.h + dy }));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setGeom((current) => {
        localStorage.setItem(GEOM_KEY, JSON.stringify(current));
        return current;
      });
      drag.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  const position = canvas?.position as { wipPence?: number; draftPence?: number; arPence?: number; apPence?: number; cashMovementPence?: number; customers?: { customerId: string; customer: string; wipPence: number }[] } | undefined;
  const quotation = canvas?.quotation as { id?: string; number?: string; customer?: string; title?: string; amountPence?: number } | undefined;
  const home = canvas?.home as { uninvoicedPence?: number; rows?: unknown[] } | undefined;
  const report = canvas as { spec?: { query?: string }; data?: { kpis?: { label: string; value: unknown }[] } } | null;

  return (
    <>
      {collaborateOpen ? (
        <>
        <button type="button" aria-label="Close agents" className="fixed inset-0 z-40 bg-gray-900/40 md:hidden" onClick={closeCollaborate} />
        <div className="bo-agent-dock fixed z-50 flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-900" style={{ left: geom.x, top: geom.y, width: geom.w, height: geom.h }}>
          <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-gray-300 md:hidden dark:bg-gray-600" />
          <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700" onPointerDown={(event) => { if ((event.target as HTMLElement).closest('select,button,a,input,textarea')) return; beginDrag('move', event); }}>
            <GripHorizontal className="hidden h-4 w-4 cursor-move text-gray-400 md:block" />
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-sm font-semibold">{sessions.find((row) => row.id === conversationId)?.title || selected?.title || 'New chat'}</h3>
              <p className="truncate text-[11px] text-gray-500">{runLabel || 'Runtime: waiting'}{selected ? ` · ${toolCount} tools` : ''}</p>
            </div>
            <Select searchable className="hidden h-8 w-40 md:block" value={role} onChange={(event) => { setRole(event.target.value); newChat(); loadSessions(event.target.value); }}>
              {roles.map((item) => <option key={item.code} value={item.code}>{item.title}</option>)}
            </Select>
            <Button variant="ghost" size="icon" className="shrink-0" aria-label="Close" onClick={closeCollaborate}><X className="h-4 w-4" /></Button>
          </div>
          <div className="shrink-0 border-b border-gray-200 px-4 py-2 dark:border-gray-700 md:hidden">
            <Select searchable className="h-8 w-full" value={role} onChange={(event) => { setRole(event.target.value); newChat(); loadSessions(event.target.value); }}>
              {roles.map((item) => <option key={item.code} value={item.code}>{item.title}</option>)}
            </Select>
          </div>
          {error ? <div className="shrink-0 border-t border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}
          <div className="flex min-h-0 flex-1 max-md:flex-col">
            <aside className="flex w-full shrink-0 flex-col border-b border-gray-200 bg-gray-50 max-md:max-h-40 dark:border-gray-700 dark:bg-gray-950 md:w-52 md:border-b-0 md:border-r">
              <Button variant="outline" size="sm" className="m-2 justify-start gap-1" onClick={newChat}><Plus className="h-3.5 w-3.5" />New chat</Button>
              <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
                {sessions.map((row) => (
                  <button key={row.id} type="button" className={`mb-1 w-full rounded-lg px-2 py-2 text-left text-xs ${row.id === conversationId ? 'bg-violet-100 text-gray-900 dark:bg-violet-900/40 dark:text-gray-100' : 'hover:bg-gray-200/80 dark:hover:bg-gray-800'}`} onClick={() => void apiGet<ChatSessionRow>(`/api/chat/sessions/${row.id}`).then((item) => {
                    setConversationId(item.id);
                    setRole(item.roleCode || role);
                    setRunMeta({ model: item.model, agentId: item.agentId, runtime: item.runtime });
                    setMessages((item.messages || []).map((message, index) => ({ id: `${item.id}-${index}`, role: message.role, text: message.text, events: message.events })));
                    scrollDown();
                  })}>
                    <div className="line-clamp-2 font-medium">{row.title}</div>
                    <div className="mt-1 text-[10px] text-gray-400">{row.runtime === 'cloud' ? 'Running' : row.model || 'local'}</div>
                  </button>
                ))}
              </div>
            </aside>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div ref={logRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 text-sm">
              {!messages.length ? (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm text-gray-500">
                  <MessageSquare className="h-10 w-10 opacity-40" />
                  <p>Select a chat or start a new one.</p>
                </div>
              ) : null}
              {messages.map((message) => message.role === 'user' ? (
                <div key={message.id} className="flex flex-col items-end gap-1">
                  <div className="max-w-[85%] rounded-2xl bg-gray-200 px-4 py-2.5 text-sm text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                    <p className="whitespace-pre-wrap">{message.text}</p>
                  </div>
                  {message.files?.length ? <div className="flex max-w-[85%] flex-wrap justify-end gap-1.5">{message.files.map((name) => <span key={name} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-500 dark:border-gray-700 dark:bg-gray-900">{name}</span>)}</div> : null}
                </div>
              ) : (
                <div key={message.id} className="flex flex-col items-start gap-1">
                  <div className="w-full max-w-[92%] text-sm text-gray-900 dark:text-gray-100">
                    {!message.text && !message.events?.length && message.streaming ? <span className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="h-4 w-4 animate-spin" />Working</span> : <ChatTranscript content={message.text} events={message.events} />}
                  </div>
                </div>
              ))}
              {canvas?.kind === 'financial-position' ? (
                <div className="rounded-xl border border-gray-200 p-4 text-xs dark:border-gray-700">
                  <div className="mb-3 flex items-center gap-2 font-semibold"><BarChart3 className="h-4 w-4 text-blue-600" /> Financial position</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>WIP {formatMoney(position?.wipPence)}</div>
                    <div>Drafts {formatMoney(position?.draftPence)}</div>
                    <div>AR {formatMoney(position?.arPence)}</div>
                    <div>AP {formatMoney(position?.apPence)}</div>
                    <div className="col-span-2">Cash movements {formatMoney(position?.cashMovementPence)}</div>
                  </div>
                  <table className="mt-3 w-full">{(position?.customers || []).map((row) => <tr key={row.customerId}><td>{row.customer}</td><td>{formatMoney(row.wipPence)}</td></tr>)}</table>
                </div>
              ) : null}
              {canvas?.kind === 'wip-board' ? <div className="rounded-xl border p-4 text-xs">WIP {formatMoney(home?.uninvoicedPence)} · {home?.rows?.length || 0} lines</div> : null}
              {canvas?.kind === 'quotation' ? (
                <div className="rounded-xl border p-4 text-xs">
                  <div className="font-semibold">{quotation?.number} · {quotation?.customer || quotation?.title}</div>
                  <div>{formatMoney(quotation?.amountPence)}</div>
                  <div>{quotation?.title}</div>
                  {quotation?.id ? <Link className="underline" to={`/quotations/${quotation.id}`}>Open</Link> : null}
                </div>
              ) : null}
              {canvas?.kind === 'report' ? (
                <div className="rounded-xl border p-4 text-xs">
                  <div className="font-semibold">{report?.spec?.query}</div>
                  {(report?.data?.kpis || []).map((kpi) => <div key={kpi.label}>{kpi.label}: {String(kpi.value)}</div>)}
                </div>
              ) : null}
              {confirm ? (
                <div className="rounded-lg border border-amber-400 bg-amber-50 p-4 text-xs dark:bg-amber-950">
                  <div className="flex items-center gap-2 font-semibold"><TriangleAlert className="h-4 w-4" /> Confirm {confirm.tool}</div>
                  <Button size="sm" className="mt-3 bg-amber-500 hover:bg-amber-600" onClick={() => void doConfirm()}>Confirm</Button>
                </div>
              ) : null}
            </div>
            <div className="shrink-0 space-y-2 border-t border-gray-200 p-3 dark:border-gray-700">
              {files.length ? <div className="flex flex-wrap gap-1.5">{files.map((file) => <span key={file.name} className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2 py-1 text-[11px] text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300"><Paperclip className="h-3 w-3" />{file.name}<button type="button" aria-label={`Remove ${file.name}`} onClick={() => setFiles((rows) => rows.filter((item) => item.name !== file.name))}><X className="h-3 w-3" /></button></span>)}</div> : null}
              <TextArea placeholder={conversationId ? 'Add a follow up' : 'Message this agent'} value={draft} disabled={sending} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void send(); } }} />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <input ref={fileRef} className="hidden" type="file" multiple accept=".pdf,.txt,.md,.csv,.json,.docx,.xlsx,image/*" onChange={(event) => { pickFiles(event.target.files); event.target.value = ''; }} />
                  <Button type="button" size="sm" variant="outline" className="gap-1.5 px-2 text-[11px]" disabled={sending} onClick={() => fileRef.current?.click()}><Paperclip className="h-3.5 w-3.5" />Attach</Button>
                  <Button type="button" size="icon" variant="outline" aria-label="Voice input" onClick={() => {
                    const Rec = window.webkitSpeechRecognition || window.SpeechRecognition;
                    if (!Rec) { setError('Browser speech recognition is not available.'); return; }
                    const rec = new Rec();
                    rec.onresult = (event) => setDraft(event.results[0][0].transcript);
                    rec.start();
                  }}><Mic className="h-3.5 w-3.5" /></Button>
                  <Link className="text-[11px] text-gray-500 underline" to="/agents">{toolCount} tools</Link>
                </div>
                <Button size="sm" disabled={sending || (!draft.trim() && !files.length)} onClick={() => void send()}>{sending ? 'Working' : 'Send'}</Button>
              </div>
            </div>
            </div>
          </div>
          <div className="absolute bottom-0 right-0 hidden h-5 w-5 cursor-se-resize md:block" aria-label="Resize" onPointerDown={(event) => { event.preventDefault(); event.stopPropagation(); beginDrag('resize', event); }} />
        </div>
        </>
      ) : null}
    </>
  );
}
